import i18n from "@/i18n";
import { ApiApprovalRequest } from "@/types/approvals/approval.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiCurrency } from "@/types/currency.types.ts";
import { ApiDepartmentShort } from "@/types/department.types.ts";
import { ApiDivisionShort } from "@/types/division.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase, ApiPagedData } from "@skailer-com/uikit/core";
import { ApiJobProfile } from "@/types/job-profile.types.ts";
import { ApiLocation } from "@/types/location.types.ts";
import { ApiApplicantShort } from "@/types/recruit/applicant.types.ts";
import { ApiJobShort } from "@/types/recruit/job.types.ts";
import { SelectItem, TagVariants } from "@skailer-com/uikit";

export interface ApiHiringPlan {
  id: number;
  title: string;
  description: string;
  responsibilities: string | null;
  requirements: string | null;
  salary_min: string | null;
  salary_max: string | null;
  currency: ApiCurrency | null;
  division: ApiDivisionShort | null;
  department: ApiDepartmentShort | null;
  job_profile: ApiJobProfile | null;
  location: ApiLocation | null;
  created_by: ApiEmployeeShort;
  date_plan: Date | null;
  job?: ApiJobShort;
  files: ApiFile[];
  status: ApiHiringPlanStatus;
  hired_applicants: ApiApplicantShort[];
  datetime_created_at: Date;
}

export interface ApiHiringPlanPayload {
  title: string;
  description: string | null;
  responsibilities: string | null;
  requirements: string | null;
  salary_min: number | null;
  salary_max: number | null;
  division_id: number | null;
  department_id: number | null;
  currency_code: string | null;
  job_profile_id: number | null;
  location_id: number | null;
  date_plan: Date | null;
  file_uuids: string[] | null;
}

export enum ApiHiringPlanStatus {
  awaiting = "awaiting",
  approved = "approved",
  rejected = "rejected",
  canceled = "canceled",
  hiring = "hiring",
  hired = "hired",
}

export interface ApiHiringPlanApproveRequest {
  is_bypass_approval_flow?: boolean;
  comment?: string | null;
}

export interface ApiHiringPlanConfirmationCommentUpdate {
  comment: string | null;
}

export interface ApiHiringPlanFilter extends ApiFilterBase {
  text?: string;
  statuses?: ApiHiringPlanStatus[];
  department_ids?: number[];
  location_ids?: number[];
  division_ids?: number[];
  created_by_ids?: number[];
  date_plan_to?: Date;
  date_plan_from?: Date;
}

export interface ApiHiringPlanRequest extends ApiApprovalRequest {
  hiring_plan_data: ApiHiringPlan;
}

export interface ApiPagedHiringPlanData extends ApiPagedData<ApiHiringPlanRequest> {
  awaiting_count: number;
}

export const getHiringPlanRequestStatuses = (): SelectItem[] => {
  return [
    ApiHiringPlanStatus.approved,
    ApiHiringPlanStatus.awaiting,
    ApiHiringPlanStatus.rejected,
    ApiHiringPlanStatus.canceled,
    ApiHiringPlanStatus.hiring,
    ApiHiringPlanStatus.hired,
  ].map((item) => ({
    id: item,
    title: getHiringPlanRequestStatusTitle(item),
  }));
};

export const getHiringPlanRequestStatusTitle = (status: ApiHiringPlanStatus) => {
  const statusNames: Record<ApiHiringPlanStatus, string> = {
    [ApiHiringPlanStatus.approved]: i18n.t("approval:request_status_approved"),
    [ApiHiringPlanStatus.awaiting]: i18n.t("approval:request_status_awaiting"),
    [ApiHiringPlanStatus.rejected]: i18n.t("approval:request_status_rejected"),
    [ApiHiringPlanStatus.canceled]: i18n.t("approval:request_status_canceled"),
    [ApiHiringPlanStatus.hiring]: i18n.t("hiring_plan:request_status_hiring"),
    [ApiHiringPlanStatus.hired]: i18n.t("hiring_plan:request_status_hired"),
  };

  return statusNames[status];
};

export const getHiringPlanRequestStatusColor = (status: ApiHiringPlanStatus) => {
  let textColor = "";
  let tagVariant: TagVariants = "positive";

  switch (status) {
    case ApiHiringPlanStatus.approved:
    case ApiHiringPlanStatus.hiring:
      tagVariant = "positive";
      textColor = "text-positive";
      break;
    case ApiHiringPlanStatus.awaiting:
      tagVariant = "warning";
      textColor = "text-warning";
      break;
    case ApiHiringPlanStatus.hired:
    case ApiHiringPlanStatus.canceled:
      tagVariant = "secondary";
      textColor = "text-secondary";
      break;
    case ApiHiringPlanStatus.rejected:
      tagVariant = "danger";
      textColor = "text-danger";
      break;
  }

  return { tagVariant, textColor };
};
